import * as d3 from 'd3';
import { differenceInMilliseconds } from 'date-fns';
import { compact, filter, isEmpty, isNil, isUndefined, map, minBy, stubTrue, uniq } from 'lodash';
import { ReactNode } from 'react';

import { UserDisplayPreferences } from '@/api/userAccount';
import {
    ChartTemplateProps,
    addAlertLinesToSvg,
    captureChartTeardown,
    createChartTemplate,
    frameCoalescedReporter,
    pointerGestureReporter,
} from '@/charts/common';
import { GenericObject, findRelativeAncestor, getXValue, getYValue, positionCursorTooltip } from '@/charts/helpers';
import { AlertLine, D3SvgSelection, MarginConfig, PointerInteractionSource } from '@/charts/types';
import { Units } from '@/common/convert';
import { renderUnit } from '@/components/data/Datum';
import { formatTimestamp } from '@/components/data/Timestamp';
import { DateRange } from '@/store';
import { isFiniteNumber } from '@/utils';

export const DOT_RADIUS = 2;

/**
 * We'll consider a timestamp "close enough" to count as a given hover/selection if it's within this distance
 */
const TIMESTAMP_WINDOW_PIXELS = 12;

type D3AttrValue<D> = Parameters<d3.Selection<d3.BaseType, D, d3.BaseType, unknown>['attr']>[1];

export type DotRendererParams<D> = {
    target: d3.Selection<d3.EnterElement, D, SVGGElement, unknown>;
    x: D3AttrValue<D>;
    y: D3AttrValue<D>;
    dataset: D[];
    line: Line<D>;
};

export type Line<D> = {
    id: string;
    label: string;
    sensorLabel?: string;
    valueKey: keyof D extends string ? keyof D : string;
    dataFilter?: (datum: D) => boolean;
    datumDetailRenderer?: (datum: D, line: Line<D>) => ReactNode;
    dotRenderer?: (params: DotRendererParams<D>) => void;
    renderMissingValueDot?: boolean;
    color: { line: string; circle: string };
} & (
    | { unit: string; unitType: string }
    | { unitKey: keyof D extends string ? keyof D : string; unitTypeKey: keyof D extends string ? keyof D : string }
);

export type LineDataSlice<D, L extends Line<D> = Line<D>> = { datum: D; lines: L[] }[];

type RenderTemplateResult = NonNullable<ReturnType<typeof createChartTemplate>>;

export class LineChart<Datum extends GenericObject, L extends Line<Datum> = Line<Datum>> {
    protected readonly dataset: Datum[];
    protected readonly allTimestamps: string[];
    protected readonly lines: (L & { dataFilter: NonNullable<L['dataFilter']> })[];
    protected readonly yAxisLabel: string;

    protected readonly onSelectTimestamp: (timestamp: string | null) => void;
    private removeOwnedNodes: (() => void) | null = null;
    private maybeClearingSelectedTimestamp: string | null = null;
    private selectedTimestamp: Date | null = null;
    private readonly draggedTimestamp = frameCoalescedReporter<string>((timestamp) =>
        this.onSelectTimestamp(timestamp)
    );
    private readonly pointerGesture = pointerGestureReporter((source) => this.options.onUserInteraction?.(source));

    protected rendered?: {
        svg: D3SvgSelection;
        relativeAncestor: Element;
        x: d3.ScaleTime<number, number, never>;
        y: d3.ScaleLinear<number, number, never>;
        width: number;
        height: number;
        margin: MarginConfig;
        timestampWindowMs: number;
        dynamicElements?: ReturnType<RenderTemplateResult['addDynamicElements']>;
    };

    private lineHoverCircles: { [lineId: string]: d3.Selection<SVGCircleElement, unknown, HTMLElement, Datum> } = {};
    protected lineData: { [lineId: string]: Datum[] } = {};

    constructor(
        dataset: Datum[],
        lines: L[],
        protected readonly options: {
            xAxisKey: keyof Datum extends string ? keyof Datum : string;
            yAxisLabel: string;
            yAxisOptions?: ChartTemplateProps['yAxisOptions'];
            chartWidth?: number;
            chartHeight?: number;
            rangePaddingPercent?: number;
            showAlertLines?: boolean;
            scaleChartToFitAllAlerts?: boolean;
            alertLines?: AlertLine[];
            dateRange?: DateRange | null;
            timeZone: UserDisplayPreferences['timeZone'];
            onSelectData?: (data: LineDataSlice<Datum, L> | null) => void;
            onSelectTimestamp?: (timestamp: string | null, timeline?: string[]) => void;
            onUserInteraction?: (source: PointerInteractionSource) => void;
        }
    ) {
        const { xAxisKey } = this.options;

        this.onSelectTimestamp = (selectedTimestamp) =>
            options.onSelectTimestamp?.(selectedTimestamp, this.allTimestamps);

        this.lines = lines.map((line: L) => {
            const baseDataFilter = line.dataFilter ?? stubTrue;
            const dataFilter = line.renderMissingValueDot
                ? baseDataFilter
                : (d: Datum) => baseDataFilter(d) && isFiniteNumber(d[line.valueKey]);
            return {
                ...line,
                dataFilter,
            };
        });

        // sorting the dataset by timestamp is needed for the mouse over effect to allow bisect to find nearest datapoint
        this.dataset = dataset.toSorted(
            (a, b) => new Date(getXValue(a, xAxisKey)).valueOf() - new Date(getXValue(b, xAxisKey)).valueOf()
        );

        this.lineData = Object.fromEntries(
            this.lines.map(({ id, dataFilter }) => {
                return [id, filter(this.dataset, dataFilter)];
            })
        );

        this.allTimestamps = Object.values(this.lineData).flatMap((d) => map(d, xAxisKey) as string[]);

        let yUnits: Units | undefined = undefined;
        for (const [lineId, data] of Object.entries(this.lineData)) {
            const line = this.lineById(lineId);
            if ('unit' in line) {
                yUnits = line.unit as Units;
                break;
            }
            const unit = data.find((d) => line.unitKey in d);
            if (unit) {
                yUnits = unit[line.unitKey] as Units;
                break;
            }
        }
        this.yAxisLabel = this.options.yAxisLabel + (yUnits ? ` (${renderUnit(yUnits)})` : '');
    }

    lineById(lineId: string): L {
        const line = this.lines.find((l) => l.id === lineId);
        if (!line) {
            throw new Error(`Line not found: ${lineId}`);
        }
        return line;
    }

    /**
     *
     * @param line
     * @param data dataset already filtered to points that should be rendered on the line
     */
    getYRange(line: L, data: Datum[]): { min: number; max: number } | null {
        const [min, max] = d3.extent(data, (datum) =>
            isFiniteNumber(datum[line.valueKey]) ? getYValue(datum, line.valueKey) : undefined
        );
        if (isUndefined(min) || isUndefined(max)) {
            return null;
        }
        return { min, max };
    }

    getDataYRange() {
        let yMin;
        let yMax;

        for (const [lineId, data] of Object.entries(this.lineData)) {
            const line = this.lineById(lineId);
            const { min, max } = this.getYRange(line, data) ?? {
                yMin: undefined,
                yMax: undefined,
            };
            if (!isNil(min)) {
                if (isNil(yMin)) {
                    yMin = min;
                } else {
                    yMin = Math.min(yMin, min);
                }
            }
            if (!isNil(max)) {
                if (isNil(yMax)) {
                    yMax = max;
                } else {
                    yMax = Math.max(yMax, max);
                }
            }
        }

        return { yMin: yMin ?? 0, yMax: yMax ?? 0 };
    }

    // https://d3js.org/d3-array/bisect#bisector_left
    // find the index of the data point with matching timestamp
    readonly dateBisector = d3.bisector<Datum, Date>((d) => new Date(getXValue(d, this.options.xAxisKey)));

    destroy() {
        this.draggedTimestamp.cancel();
        this.pointerGesture.cancel();

        const listeningRect = this.rendered?.dynamicElements?.listeningRect;
        listeningRect?.on('mousemove', null).on('mouseleave', null).on('mousedown', null).on('click', null);

        this.removeOwnedNodes?.();
        this.removeOwnedNodes = null;

        this.rendered = undefined;
        this.lineHoverCircles = {};
        this.lineData = {};
        // `dataset` is this chart's own sorted copy and the other two are derived from it, so emptying
        // them in place is safe and keeps the fields' declared variance
        this.allTimestamps.length = 0;
        this.dataset.length = 0;
    }

    render(containerId: string) {
        const { showAlertLines, scaleChartToFitAllAlerts, alertLines, dateRange, timeZone } = this.options;
        const { lines, dataset } = this;
        const { yAxisLabel } = this;

        if (!this.dataset || isEmpty(this.dataset)) {
            return;
        }

        const rendered = createChartTemplate({
            ...this.options,
            scaleChartToFitAllAlerts: showAlertLines && scaleChartToFitAllAlerts,
            lines,
            dataset,
            yAxisLabel,
            dataYRange: this.getDataYRange(),
            containerId,
            timeZone,
            dateRange,
        });

        if (!rendered) {
            return;
        }

        const { addDynamicElements, svg, x, y, width, height, margin } = rendered;

        const timestampWindowMs = x.invert(TIMESTAMP_WINDOW_PIXELS).getTime() - x.invert(0).getTime();
        const relativeAncestor = findRelativeAncestor(svg.node()!);

        this.rendered = { svg, x, y, width, height, margin, timestampWindowMs, relativeAncestor };

        const dataLines = [];
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];

            const dataForChannel = this.lineData[line.id];
            if (!dataForChannel) {
                continue;
            }
            dataLines.push({ line, data: dataForChannel });
        }

        this.renderLines(dataLines.reverse());

        // Add elements that appear on hover and are one for the entire chart
        // This has to be done after the circles and activeLines are created so that they are on top of the activeLines and circles
        this.rendered.dynamicElements = addDynamicElements();
        this.removeOwnedNodes = captureChartTeardown(containerId);

        // Alert lines above everything else: they'll be translucent most of the time but need not to be obscured on hover
        if (showAlertLines && alertLines) {
            addAlertLinesToSvg({
                svg,
                y,
                width,
                height,
                alertLines,
            });
        }

        const { listeningRect } = this.rendered.dynamicElements;
        listeningRect.on('mousemove', (e) => this.onMouseMove(e));
        listeningRect.on('mouseleave', () => this.onMouseLeave());
        listeningRect.on('mousedown', (e) => this.onMouseDown(e));
        listeningRect.on('click', (e) => this.onClick(e));
    }

    renderLines(dataLines: { line: L; data: Datum[] }[]) {
        dataLines.forEach(({ line, data }) => this.renderLine(line, data));
    }

    renderLine(line: L, data: Datum[]) {
        if (!this.rendered) return;
        /*
        Things that need to be done for each line:
        - create line generator
        - create circles
        - add circle hover marker
        */

        const { xAxisKey } = this.options;
        const { svg, x, y } = this.rendered;
        const color = line.color;
        const renderedData = line.renderMissingValueDot ? data : data.filter((d) => isFiniteNumber(d[line.valueKey]));
        // Create the line generator
        const lineGenerator = d3
            .line<Datum>()
            .defined((d) => isFiniteNumber(d[line.valueKey]))
            .x((datum) => x(new Date(getXValue(datum, xAxisKey))))
            .y((datum) => y(getYValue(datum, line.valueKey)));
        // Add the line
        svg.append('path')
            .datum(data)
            .attr('fill', 'none')
            .attr('stroke', color.line)
            .attr('stroke-width', 1.25)
            .attr('d', lineGenerator);
        // Add circle hover marker
        const circle = svg
            .append('circle')
            .attr('r', 0)
            .attr('fill', color.circle)
            .style('stroke', color.circle)
            .attr('opacity', 0.7)
            .style('pointer-events', 'none');
        this.lineHoverCircles[line.id] = circle;

        // Add dots for a scatter plot. Dots signify the data points and allow to identify gaps in the data
        const dotTarget = svg.append('g').selectAll('dot').data(renderedData).enter();

        const dotRenderer = line.dotRenderer ?? defaultDotRenderer;
        dotRenderer({
            target: dotTarget,
            x: function (datum: Datum) {
                return x(new Date(getXValue(datum, xAxisKey)));
            },
            y: function (datum: Datum) {
                return y(getYValue(datum, line.valueKey));
            },
            dataset: renderedData,
            line,
        });
    }

    drawHoverEffects(timestamp: string) {
        if (!this.rendered?.dynamicElements) return;

        const {
            x,
            y,
            margin,
            timestampWindowMs,
            relativeAncestor,
            dynamicElements: { listeningRect, tooltip, hoverLine },
        } = this.rendered;
        const { xAxisKey, timeZone } = this.options;

        const listeningRectNode = listeningRect.node();
        if (!listeningRectNode) {
            return;
        }

        const timestampDate = new Date(timestamp);
        for (let i = 0; i < this.lines.length; i++) {
            const line = this.lines[i];
            const circle = this.lineHoverCircles[line.id];
            const dataForChannel = this.lineData[line.id].filter(
                (d) => line.renderMissingValueDot || isFiniteNumber(d[line.valueKey])
            );
            const datumIndex = this.dateBisector.center(dataForChannel, timestampDate);
            const d = dataForChannel[datumIndex];
            const xDate = new Date(getXValue(d, xAxisKey));
            if (
                !d ||
                !isFiniteNumber(d[line.valueKey]) || // not rendering a hover circle in the case of renderMissingValueDot since we don't know what Y to place it at
                Math.abs(differenceInMilliseconds(timestampDate, xDate)) > timestampWindowMs
            ) {
                // No datapoint for this channel for the selected timestamp
                circle.attr('r', 0);
                continue;
            }

            const xPos = x(xDate);
            const yPos = y(getYValue(d, line.valueKey));
            circle.attr('cx', xPos).attr('cy', yPos).attr('r', 5);
        }

        const listeningRectDimensions = listeningRectNode.getBoundingClientRect();
        const cursorX = x(timestampDate);

        // Add in the tooltip
        tooltip.html(`<span style="color: #707070">${formatTimestamp(timestampDate, { timeZone })}</span>`);
        positionCursorTooltip({
            tooltip,
            cursorX,
            chartBounds: listeningRectDimensions,
            relativeAncestor,
            margin,
        });
        hoverLine.attr('x1', cursorX).attr('x2', cursorX);

        tooltip.style('visibility', 'visible');
        hoverLine.style('visibility', 'visible');
    }

    hideHoverEffects() {
        if (!this.rendered?.dynamicElements) return;

        const { tooltip, hoverLine } = this.rendered.dynamicElements;
        tooltip.style('visibility', 'hidden');
        hoverLine.style('visibility', 'hidden');
        Object.values(this.lineHoverCircles).map((c) => c.attr('r', 0));
    }

    dataForTimestamp(timestamp: Date) {
        const { xAxisKey } = this.options;
        const selectedData = compact(
            Object.entries(this.lineData).map(([lineId, dataForLine]) => {
                const i = this.dateBisector.center(dataForLine, timestamp);
                const d = dataForLine[i];
                if (
                    d &&
                    Math.abs(differenceInMilliseconds(timestamp, new Date(getXValue(d, xAxisKey)))) <=
                        (this.rendered?.timestampWindowMs ?? 60000)
                ) {
                    return { line: this.lineById(lineId), datum: d };
                }
            })
        );

        return uniq(map(selectedData, 'datum')).map((datum) => ({
            datum,
            lines: map(filter(selectedData, { datum }), 'line'),
        }));
    }

    findTimestampFromMouseEvent(e: MouseEvent) {
        if (!this.rendered) return;

        const { x } = this.rendered;
        const { xAxisKey } = this.options;
        const [xCoord] = d3.pointer(e, e.target);
        const timestamp = x.invert(xCoord);

        const timestamps: string[] = [];
        this.dataForTimestamp(timestamp).forEach(({ datum }) => {
            if (datum) {
                timestamps.push(getXValue(datum, xAxisKey));
            }
        });
        // return closest candidate timestamp
        const timestampMillis = timestamp.valueOf();
        return minBy(timestamps, (t) => Math.abs(new Date(t).valueOf() - timestampMillis));
    }

    onMouseMove(e: MouseEvent) {
        if (!this.rendered) return;

        const { svg } = this.rendered;

        const timestamp = this.findTimestampFromMouseEvent(e);
        if (timestamp) {
            svg.style('cursor', 'pointer');
            this.drawHoverEffects(timestamp);
            if (e.buttons & 1) {
                // if primary button is down, drag the selected timestamp along with us
                this.draggedTimestamp.send(timestamp);
                this.pointerGesture.drag(timestamp);
            }
        } else {
            svg.style('cursor', 'default');
            this.hideHoverEffects();
        }
    }

    onMouseDown(e: MouseEvent) {
        this.draggedTimestamp.cancel();
        this.maybeClearingSelectedTimestamp = null;
        const timestamp = this.findTimestampFromMouseEvent(e);
        if (timestamp) {
            const isOnSelection = timestamp === this.selectedTimestamp?.toISOString();
            this.pointerGesture.press(timestamp, { isOnSelection });
            if (isOnSelection) {
                this.maybeClearingSelectedTimestamp = timestamp;
            } else {
                this.onSelectTimestamp(timestamp);
            }
        }
    }

    onClick(e: MouseEvent) {
        const timestamp = this.findTimestampFromMouseEvent(e);
        if (
            timestamp &&
            timestamp === this.maybeClearingSelectedTimestamp &&
            timestamp === this.selectedTimestamp?.toISOString()
        ) {
            this.onSelectTimestamp(null);
        }
    }

    onMouseLeave() {
        this.hideHoverEffects();
    }

    clearSelectedTimestamp() {
        this.selectedTimestamp = null;
        this.rendered?.dynamicElements?.selectionLine.style('visibility', 'hidden');
        this.options.onSelectData?.(null);
    }

    setSelectedTimestamp(timestamp: Date | null) {
        if (!this.rendered?.dynamicElements) return;

        if (!timestamp) {
            this.clearSelectedTimestamp();
            return;
        }

        const {
            x,
            dynamicElements: { selectionLine },
        } = this.rendered;

        const selectedData = this.dataForTimestamp(timestamp);

        if (!selectedData.length) {
            this.clearSelectedTimestamp();
            return;
        }

        this.selectedTimestamp = timestamp;
        const xCoord = x(timestamp);
        selectionLine.style('visibility', 'visible').attr('x1', xCoord).attr('x2', xCoord);

        this.options.onSelectData?.(selectedData);
    }
}

export const drawLineChart = <D extends GenericObject, L extends Line<D> = Line<D>>({
    containerId,
    dataset,
    lines,
    ...options
}: ConstructorParameters<typeof LineChart<D, L>>[2] & { containerId: string; dataset: D[]; lines: L[] }) => {
    const lineChart = new LineChart(dataset, lines, options);
    lineChart.render(containerId);
    return lineChart;
};

export function defaultDotRenderer<Datum>({ target, x, y, line: { color, valueKey } }: DotRendererParams<Datum>) {
    target
        .append('circle')
        .style('display', (datum) => (isFiniteNumber(datum[valueKey as keyof Datum]) ? 'initial' : 'none'))
        .attr('cx', x)
        .attr('cy', y)
        .attr('r', DOT_RADIUS)
        .style('fill', color.line);
}
